<template lang="pug">
    v-dialog(
        v-model="isOpen"
        width="520"
        persistent
    )
        div.dialogWrapper
            v-form.c-form(autocomplete="off" @submit.prevent="submit")
                v-container(fluid grid-list-lg)
                    v-layout.align-center(raw wrap)

                        v-flex(xs10)

                            // Title
                            title-component(:title="$t(i18nPrefix + '.title')" type="h3")

                        v-flex.text-xs-right.mb-2(xs2)

                            // Close
                            button-icon(:action="cancel" color="white" icon="close")

                        v-flex(xs12)

                            // Title field
                            v-text-field(
                                :error-messages="errors.collect('title')"
                                :data-vv-as="$t(i18nPrefix + '.titleField')"
                                :label="$t(i18nPrefix + '.titleField')"
                                :counter="titleMax"
                                :maxlength="titleMax"
                                name="title"
                                v-model="form.title"
                                v-validate="`required|max:${titleMax}`"
                                outline
                            )

                        v-flex(xs12)

                            // Message field
                            v-textarea(
                                :error-messages="errors.collect('message')"
                                :data-vv-as="$t(i18nPrefix + '.messageField')"
                                :label="$t(i18nPrefix + '.messageField')"
                                :counter="messageMax"
                                :maxlength="messageMax"
                                name="message"
                                v-model="form.message"
                                v-validate="`required|max:${messageMax}`"
                                rows="3"
                                outline
                            )

                        v-flex(xs12)

                            // Target session id (optional). Entering one
                            // clears the other field so only one deep-link
                            // target ships in the payload.
                            v-text-field(
                                :label="$t(i18nPrefix + '.sessionField')"
                                :disabled="isSending"
                                name="sessionId"
                                clearable
                                outline
                                v-model="form.sessionId"
                                @input="onSessionPicked"
                            )

                        v-flex(xs12)

                            // Target mindful moment id (optional).
                            v-text-field(
                                :label="$t(i18nPrefix + '.mindfulMomentField')"
                                :disabled="isSending"
                                :hint="$t(i18nPrefix + '.targetHint')"
                                persistent-hint
                                name="mindfulMomentId"
                                clearable
                                outline
                                v-model="form.mindfulMomentId"
                                @input="onMindfulMomentPicked"
                            )

                        v-flex(xs12)

                            separator-component

                            div.controls
                                div

                                    // Cancel
                                    button-component.ml-0(
                                        :action="cancel"
                                        :isLoading="isSending"
                                        :disabled="isSending"
                                        color="grey"
                                        large
                                        outline
                                    ) {{ $t(i18nPrefix + '.cancel') }}

                                div

                                    // Send
                                    button-component.mr-0.submit(
                                        :action="submit"
                                        :isLoading="isSending"
                                        :disabled="isSending"
                                        color="white"
                                        large
                                    ) {{ $t(i18nPrefix + '.send') }}
</template>

<script>
    import VueTypes from 'vue-types';
    import users from '~api/users';
    import businesses from '~api/businesses';
    import { Handler } from '~services';
    import Button from '~components/Button';
    import ButtonIcon from '~components/ButtonIcon';
    import Separator from '~components/Separator';
    import Title from '~components/Title';

    const TITLE_MAX = 65;
    const MESSAGE_MAX = 178;
    const TARGET_USER = 'user';
    const TARGET_BUSINESS = 'business';

    export default {
        $_veeValidate: {
            validator: 'new'
        },
        components: {
            ButtonIcon,
            ButtonComponent: Button,
            SeparatorComponent: Separator,
            TitleComponent: Title
        },
        props: {
            value: VueTypes.bool.def(false),
            userId: VueTypes.oneOfType([String, Number]).def(''),
            target: VueTypes.oneOf([TARGET_USER, TARGET_BUSINESS]).def(
                TARGET_USER
            )
        },
        data() {
            return {
                form: {
                    title: '',
                    message: '',
                    sessionId: '',
                    mindfulMomentId: ''
                },
                isSending: false
            };
        },
        computed: {
            isOpen: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                }
            },
            titleMax() {
                return TITLE_MAX;
            },
            messageMax() {
                return MESSAGE_MAX;
            },
            i18nPrefix() {
                return this.target === TARGET_BUSINESS
                    ? 'business.push'
                    : 'users.push';
            }
        },
        watch: {
            value(opened) {
                if (opened) {
                    this.reset();
                }
            }
        },
        methods: {
            reset() {
                this.form.title = '';
                this.form.message = '';
                this.form.sessionId = '';
                this.form.mindfulMomentId = '';
                this.$nextTick(() => {
                    this.$validator.reset();
                });
            },

            cancel() {
                this.isOpen = false;
            },

            // The deep-link target is mutually exclusive: entering one id
            // clears the other so the API only ever receives one.
            onSessionPicked(value) {
                if (value) this.form.mindfulMomentId = '';
            },

            onMindfulMomentPicked(value) {
                if (value) this.form.sessionId = '';
            },

            async submit() {
                const isValid = await this.$validator.validateAll();
                if (!isValid || !this.userId) return;

                const sendFn =
                    this.target === TARGET_BUSINESS
                        ? businesses.sendPush
                        : users.sendPush;

                const payload = {
                    title: this.form.title,
                    message: this.form.message
                };
                const sessionId = (this.form.sessionId || '').trim();
                const mindfulMomentId = (
                    this.form.mindfulMomentId || ''
                ).trim();
                if (sessionId) {
                    payload.sessionId = sessionId;
                } else if (mindfulMomentId) {
                    payload.mindfulMomentId = mindfulMomentId;
                }

                this.isSending = true;
                try {
                    const resp = await sendFn(this.userId, payload);

                    Handler.handleResponse(
                        resp,
                        this.$t(this.i18nPrefix + '.successMessage')
                    );

                    if (resp && resp.ok) {
                        this.isOpen = false;
                    }
                } finally {
                    this.isSending = false;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl';

    .dialogWrapper
        background: $theme.primary
</style>
